// BL-177 Step 2d (section 10): message signatures.
//
// Two parts, always in this order: the PERSON's signature, then the COMPANY's.
//   - Added to every eligible text and email staff write, including scheduled messages at send time.
//   - Never on WhatsApp templates (wording fixed by Meta) or automatic notifications.
//   - "Remove mine" drops the person's part for one message; not offered when the company makes it Required.
//   - The company part can't be removed while the company has it Always on.
//   - Text/WhatsApp signatures must be plain GSM-7 characters (so a fancy dash can't turn one text into two);
//     email signatures can use anything.
//   - The character / segment counter always INCLUDES the signature.
import { formatPhone } from './phone';
import {
    PERSONAL_MODES, SIGNATURE_SEPARATOR, TEXT_CHANNELS, appendSignature, assembleSignature as assembleSignatureCore, characterCount, clean,
    companySettingsFromRow, emailCompanyLines as emailCompanyLinesCore, normalizeChannel, personalMode,
} from './signatureCore';
export { PERSONAL_MODES, SIGNATURE_SEPARATOR, TEXT_CHANNELS, appendSignature, characterCount, companySettingsFromRow };
export { clean, normalizeChannel, personalMode };
// The browser formats the company phone through the one phone helper; the Edge Functions pass their own formatter.
export function emailCompanyLines(block) {
    return emailCompanyLinesCore(block, formatPhone);
}
export function assembleSignature(options = {}) {
    return assembleSignatureCore({ formatPhone, ...options });
}

// ---------------------------------------------------------------- GSM-7 ----------------------------------------
// 3GPP TS 23.038 default alphabet (basic table, minus the ESC code) and its extension table. Extension characters
// are allowed but cost 2 of the 160. Same set as SQL public.gsm7_charset() (used by is_gsm7 / gsm7_invalid_characters
// and the signature CHECKs): GSM7_BASIC + GSM7_EXTENDED (incl. form feed). The test pins both strings.
export const GSM7_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
export const GSM7_EXTENDED = '\f^{}\\[~]|€';
const BASIC = new Set(Array.from(GSM7_BASIC));
const EXTENDED = new Set(Array.from(GSM7_EXTENDED));

export function isGsm7(text) {
    return Array.from(String(text ?? '')).every((ch) => BASIC.has(ch) || EXTENDED.has(ch));
}

/** The distinct characters that are not GSM-7, in the order they first appear. */
export function gsm7InvalidChars(text) {
    const seen = [];
    Array.from(String(text ?? '')).forEach((ch) => {
        if (!BASIC.has(ch) && !EXTENDED.has(ch) && !seen.includes(ch)) seen.push(ch);
    });
    return seen;
}

// Plain replacements offered with one click. Anything not listed (emoji, other scripts) is removed by the fix.
export const GSM7_REPLACEMENTS = {
    '—': '-', // — em dash
    '–': '-', // – en dash
    '‒': '-', '‐': '-', '‑': '-', '−': '-',
    '·': '-', // · middle dot
    '•': '-', // • bullet
    '…': '...', // … ellipsis
    '‘': "'", '’': "'", '‚': "'", '′': "'", '´': "'", '`': "'",
    '“': '"', '”': '"', '„': '"', '″': '"', '«': '"', '»': '"',
    ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ', '\t': ' ',
    '™': '(TM)', '®': '(R)', '©': '(C)',
    '​': '', '﻿': '',
};

const CHAR_NAMES = {
    '—': 'long dash (—)', '–': 'dash (–)', '·': 'middle dot (·)', '•': 'bullet (•)',
    '…': 'three-dot character (…)', '‘': 'curly quote (‘)', '’': 'curly apostrophe (’)',
    '“': 'curly quote (“)', '”': 'curly quote (”)', ' ': 'non-breaking space', '\t': 'tab',
    '`': 'backtick (`)', '​': 'invisible space',
};

function describeChar(ch) {
    if (CHAR_NAMES[ch]) return CHAR_NAMES[ch];
    if (/\s/.test(ch) || /[​-‏⁠]/.test(ch)) return `special space (U+${ch.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')})`;
    return `"${ch}"`;
}

/** One character made plain: the table above, else accents stripped when that lands in GSM-7 (ç -> c), else removed. */
function plainChar(ch) {
    if (BASIC.has(ch) || EXTENDED.has(ch)) return ch;
    if (Object.prototype.hasOwnProperty.call(GSM7_REPLACEMENTS, ch)) return GSM7_REPLACEMENTS[ch];
    const stripped = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return stripped && isGsm7(stripped) ? stripped : '';
}

/** The text with every non-GSM-7 character swapped for a plain one (the one-click fix). */
export function toGsm7(text) {
    return Array.from(String(text ?? '')).map(plainChar).join('');
}

/**
 * Validation for a TEXT/WhatsApp signature. Returns null when fine, else
 * { invalid: [...chars], message: 'plain English', fixed: 'the replacement text', fixLabel }.
 */
export function textSignatureProblem(text) {
    const invalid = gsm7InvalidChars(text);
    if (invalid.length === 0) return null;
    const names = invalid.map(describeChar);
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return {
        invalid,
        message: `Text signatures can only use plain characters. Please change the ${list}: `
            + 'phones would otherwise send this as a longer, more expensive text.',
        fixed: toGsm7(text),
        fixLabel: 'Replace with plain characters',
    };
}

// ---------------------------------------------------------------- counting -------------------------------------
/** Characters as a person counts them (each line break counts as 1; an emoji counts as 1). */

/**
 * How many texts a message becomes. GSM-7: 160 in one text, 153 per part after that (extension characters cost 2).
 * Anything else switches the whole message to UCS-2: 70 in one text, 67 per part.
 */
export function segmentInfo(text) {
    const value = String(text ?? '');
    const characters = characterCount(value);
    if (isGsm7(value)) {
        const units = Array.from(value).reduce((sum, ch) => sum + (EXTENDED.has(ch) ? 2 : 1), 0);
        const segments = units === 0 ? 0 : units <= 160 ? 1 : Math.ceil(units / 153);
        return { encoding: 'GSM-7', characters, units, segments, perSegment: segments > 1 ? 153 : 160 };
    }
    const units = value.length; // UTF-16 code units: an emoji takes 2
    const segments = units === 0 ? 0 : units <= 70 ? 1 : Math.ceil(units / 67);
    return { encoding: 'UCS-2', characters, units, segments, perSegment: segments > 1 ? 67 : 70 };
}

/** The composer's counter line, signature included: "137 characters · 1 text · signature included". */
export function counterLabel(body, signature) {
    const sigText = signature?.text || '';
    const info = segmentInfo(appendSignature(body, signature));
    const texts = `${info.segments} ${info.segments === 1 ? 'text' : 'texts'}`;
    return `${info.characters} characters · ${texts}${sigText ? ' · signature included' : ''}`;
}

/** The composer counter excludes the signature from the typed count and available room. */
export function messageCharacterCounter(body, signature, channel) {
    const text = String(body ?? '');
    if (!text) return '';
    const signatureText = signature?.text || '';
    if (channel === 'whatsapp') {
        return `${characterCount(text)} of ${Math.max(0, 4096 - characterCount(signatureText))}`;
    }
    if (channel !== 'sms') return '';

    const info = segmentInfo(appendSignature(text, signature));
    const oneTextLimit = info.encoding === 'GSM-7' ? 160 : 70;
    const signatureCost = info.encoding === 'GSM-7'
        ? segmentInfo(signatureText).units
        : signatureText.length;
    const typedCost = info.encoding === 'GSM-7' ? segmentInfo(text).units : text.length;
    return `${typedCost} of ${Math.max(0, oneTextLimit - signatureCost)}`
        + (info.segments > 1 ? ` · ${info.segments} texts` : '');
}

// ---------------------------------------------------------------- assembly -------------------------------------
// The Ride's company text (the database seeds it for the company that owns 2667; everyone else starts empty).
export const DEFAULT_COMPANY_TEXT_SIGNATURE = 'The Ride ATL\n(404) 594-2667\ntherideatl.com';
// Database limits (org_signature_settings / user_signatures CHECKs).
export const TEXT_SIGNATURE_MAX = 300;
export const EMAIL_SIGNATURE_MAX = 2000;

/** Plain-English save error: 42501 = no access; 23514 = the database's own check message. */
export function signatureErrorMessage(error) {
    if (error?.code === '42501') return "You don't have access to change this.";
    return error?.message || 'Try again.';
}
// ---------------------------------------------------------------- data -----------------------------------------
export async function loadEmailCompanyOptions(client, orgId) {
    const { data, error } = await client.from('entities')
        .select('id, name, brand_name, level_type')
        .eq('org_id', orgId)
        .in('level_type', ['brand', 'company', 'organization']);
    if (error) throw error;
    const rows = data || [];
    const companies = rows.filter((row) => ['brand', 'company'].includes(row.level_type));
    const options = companies.length ? companies : rows.filter((row) => row.level_type === 'organization');
    return options.map((row) => ({
        id: row.id,
        label: row.brand_name || row.name || 'Company',
    })).filter((row) => row.id);
}

function signatureCompanyChoiceKey(orgId, userId) {
    return `intelAgentSignatureCompany:${orgId}:${userId}`;
}

function storageForSignatureChoice(storage) {
    if (storage !== undefined) return storage;
    return typeof window === 'undefined' ? null : window.localStorage;
}

export function saveActiveSignatureCompany(storage, orgId, userId, entityId) {
    if (!orgId || !userId) return;
    const target = storageForSignatureChoice(storage);
    if (!target) return;
    const key = signatureCompanyChoiceKey(orgId, userId);
    if (entityId) target.setItem(key, entityId);
    else target.removeItem(key);
}

function storedSignatureCompany(storage, orgId, userId, options) {
    if (!orgId || !userId) return '';
    const storedId = storageForSignatureChoice(storage)?.getItem(signatureCompanyChoiceKey(orgId, userId)) || '';
    return options.some((option) => option.id === storedId) ? storedId : '';
}

function entityCompanyOption(entityId, entities, optionIds) {
    const byId = new Map(entities.map((entity) => [entity.id, entity]));
    for (let entity = byId.get(entityId), hops = 0; entity && hops < 12; entity = byId.get(entity.parent_entity_id), hops += 1) {
        if (optionIds.has(entity.id)) return entity.id;
    }
    return '';
}

async function primarySendingCompany(client, orgId, options) {
    if (typeof client?.from !== 'function' || !orgId || options.length < 2) return '';
    const { data: numbers, error: numberError } = await client.from('org_twilio_numbers')
        .select('entity_id, phone_number, sms_enabled')
        .eq('org_id', orgId);
    if (numberError) throw numberError;
    const smsLines = (numbers || []).filter((line) => line.sms_enabled && line.entity_id);
    const primaryLine = smsLines.find((line) => String(line.phone_number || '').replace(/\D/g, '') === '14045942667')
        || (smsLines.length === 1 ? smsLines[0] : null);
    if (!primaryLine) return '';
    const optionIds = new Set(options.map((option) => option.id));
    if (optionIds.has(primaryLine.entity_id)) return primaryLine.entity_id;

    const { data: entities, error: entityError } = await client.from('entities')
        .select('id, parent_entity_id, level_type')
        .eq('org_id', orgId);
    if (entityError) throw entityError;
    return entityCompanyOption(primaryLine.entity_id, entities || [], optionIds);
}

/**
 * Resolves the email-company block without ever choosing the first company implicitly.
 * A door or explicitly narrowed header selection wins; an unambiguous company is automatic.
 * Otherwise the saved per-user choice wins, with the primary sending line as the initial picker value.
 */
export async function resolveActiveSignatureCompany(client, {
    orgId,
    userId,
    doorEntityId = '',
    headerEntityId = '',
    storage,
} = {}) {
    const options = await loadEmailCompanyOptions(client, orgId);
    const valid = (entityId) => options.find((option) => option.id === entityId)?.id || '';
    const doorId = valid(doorEntityId);
    if (doorId) return { options, selectedId: doorId, showPicker: false, source: 'door' };
    const headerId = valid(headerEntityId);
    if (headerId) return { options, selectedId: headerId, showPicker: false, source: 'header' };
    if (options.length === 1) return { options, selectedId: options[0].id, showPicker: false, source: 'single' };
    if (options.length < 2) return { options, selectedId: '', showPicker: false, source: 'none' };

    const savedId = storedSignatureCompany(storageForSignatureChoice(storage), orgId, userId, options);
    const lineId = savedId ? '' : await primarySendingCompany(client, orgId, options);
    return {
        options,
        selectedId: savedId || lineId,
        showPicker: true,
        source: savedId ? 'saved' : lineId ? 'primary-line' : 'choose',
    };
}

/** Brand facts for the email company block, read from Brands & Locations (entities). Read-only here. */
export async function loadEmailCompanyBlock(client, orgId, brandEntityId) {
    if (!brandEntityId) return null;
    const { data, error } = await client.from('entities')
        .select('id, name, brand_name, logo, level_type, custom')
        .eq('org_id', orgId)
        .in('level_type', ['brand', 'company', 'organization']);
    if (error) throw error;
    const rows = data || [];
    const pick = rows.find((row) => row.id === brandEntityId) || null;
    if (!pick) return null;
    const custom = pick.custom && typeof pick.custom === 'object' ? pick.custom : {};
    return {
        name: pick.brand_name || pick.name || '',
        logo_url: pick.logo || null,
        phone: custom.phone || custom.main_phone || '',
        website: custom.website || custom.site || '',
        tagline: custom.tagline || '',
    };
}

export async function loadCompanySignatureSettings(client, orgId) {
    const { data, error } = await client.from('org_signature_settings').select('*').eq('org_id', orgId).maybeSingle();
    if (error) throw error;
    return data;
}

export async function saveCompanySignatureSettings(client, orgId, patch) {
    const { error } = await client.from('org_signature_settings').upsert({ org_id: orgId, ...patch }, { onConflict: 'org_id' });
    if (error) throw error;
}

export async function loadMySignature(client, orgId, userId) {
    const { data, error } = await client.from('user_signatures').select('*').eq('org_id', orgId).eq('user_id', userId).maybeSingle();
    if (error) throw error;
    return data;
}

export async function saveMySignature(client, orgId, userId, patch) {
    const { error } = await client.from('user_signatures').upsert({ user_id: userId, org_id: orgId, ...patch }, { onConflict: 'user_id,org_id' });
    if (error) throw error;
}

/**
 * Legacy wrapper for the daily-claim RPC. Current composer and scheduled-send paths sign each eligible message directly.
 */
export async function claimSignatureSlot(client, sessionId, userId = null) {
    const args = { p_session_id: sessionId };
    if (userId) args.p_user_id = userId;
    const { data, error } = await client.rpc('claim_signature_slot', args);
    if (error) throw error;
    return data === true;
}

/** Legacy wrapper for releasing a daily-claim slot after a definite send failure. */
export async function releaseSignatureSlot(client, sessionId, userId = null) {
    const args = { p_session_id: sessionId };
    if (userId) args.p_user_id = userId;
    const { data, error } = await client.rpc('release_signature_slot', args);
    if (error) throw error;
    return data === true;
}

/**
 * Everything the composer needs in one call (RPC signature_context): the company settings with defaults filled in,
 * this person's signature, and the company time zone (for hasSignedToday's day). Shape:
 * { companySettings, personalSignature, timeZone }.
 */
export async function loadSignatureContext(client, { orgId = null, userId = null } = {}) {
    const args = {};
    if (orgId) args.p_org_id = orgId;
    if (userId) args.p_user_id = userId;
    const { data, error } = await client.rpc('signature_context', args);
    if (error) throw error;
    return {
        companySettings: companySettingsFromRow(data?.company),
        personalSignature: { text_signature: data?.personal?.text_signature || '', email_signature: data?.personal?.email_signature || '' },
        timeZone: data?.time_zone || 'America/New_York',
    };
}

/**
 * Read-only look (no claim) for the composer's preview: has this person already signed this thread today?
 * `day` is the YYYY-MM-DD the server uses (company time zone; see businessHours.localClock(...).date).
 */
export async function hasSignedToday(client, { sessionId, userId, day }) {
    const { data, error } = await client.from('signature_daily_claims')
        .select('day').eq('session_id', sessionId).eq('user_id', userId).eq('day', day).maybeSingle();
    if (error) throw error;
    return Boolean(data);
}
